import type { ProgressStepWorkflowState } from "@kontextwerk/web-sdk/core/navigation/progressSteps"

export const LOCKED_WORKFLOW_STEP_MESSAGE =
  "Bitte schließen Sie alle vorherigen Schritte ab, um in diesen Schritt gelangen zu können."

export type WorkflowProgressStateInput<StepId extends string> = {
  stepOrder: readonly StepId[]
  completedSteps?: ReadonlySet<StepId> | readonly StepId[]
  skippedSteps?: ReadonlySet<StepId> | readonly StepId[]
  selectedStep?: StepId | null
}

const hasStep = <StepId extends string>(
  source: ReadonlySet<StepId> | readonly StepId[] | undefined,
  step: StepId,
) => {
  if (!source) return false

  return "has" in source ? source.has(step) : source.includes(step)
}

export const firstCurrentWorkflowStep = <StepId extends string>({
  stepOrder,
  completedSteps,
  skippedSteps,
}: WorkflowProgressStateInput<StepId>) =>
  stepOrder.find((step) => !hasStep(completedSteps, step) && !hasStep(skippedSteps, step)) ??
  stepOrder[stepOrder.length - 1] ??
  null

export const workflowStateForStep = <StepId extends string>({
  step,
  currentStep,
  stepOrder,
  completedSteps,
  skippedSteps,
}: {
  step: StepId
  currentStep?: StepId | null
  stepOrder?: readonly StepId[]
  completedSteps?: ReadonlySet<StepId> | readonly StepId[]
  skippedSteps?: ReadonlySet<StepId> | readonly StepId[]
  selectedStep?: StepId | null
}): ProgressStepWorkflowState => {
  const resolvedCurrentStep =
    currentStep ??
    (stepOrder ? firstCurrentWorkflowStep({ stepOrder, completedSteps, skippedSteps }) : null)

  if (hasStep(skippedSteps, step)) return "skipped"

  if (hasStep(completedSteps, step)) return "completed"

  if (step === resolvedCurrentStep) return "current"

  return "locked"
}

export const selectedWorkflowStep = <StepId extends string>({
  stepOrder,
  completedSteps,
  skippedSteps,
  selectedStep,
}: WorkflowProgressStateInput<StepId>) => {
  const currentStep = firstCurrentWorkflowStep({ stepOrder, completedSteps, skippedSteps })

  if (!selectedStep || !stepOrder.includes(selectedStep)) return currentStep ?? stepOrder[0] ?? null

  const state = workflowStateForStep({
    step: selectedStep,
    currentStep,
    completedSteps,
    skippedSteps,
  })

  return state === "locked" ? currentStep : selectedStep
}

export const selectedWorkflowStepIndex = <StepId extends string>({
  stepOrder,
  completedSteps,
  skippedSteps,
  selectedStep,
}: WorkflowProgressStateInput<StepId>) => {
  const selected = selectedWorkflowStep({ stepOrder, completedSteps, skippedSteps, selectedStep })

  return Math.max(0, selected ? stepOrder.indexOf(selected) : 0)
}
